import React, { useState, useEffect } from "react";
import { Routes, Route, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, User, ChevronDown } from "lucide-react";
import Sidebar from "../components/Sidebar";
import DarkModeToggle from "../components/DarkModeToggle";
import DashboardHome from "./dashboard/DashboardHome";
import Orders from "./dashboard/Orders";
import Products from "./dashboard/Products";
import Accounting from "./dashboard/Accounting";
import Clients from "./dashboard/Clients";
import { DashboardComponentProps } from "../types/DashboardTypes";
import { supabase } from "../auth/SupabaseClient";
import ClientDetails from "./dashboard/ClientDetails";
import OrderDetails from "./dashboard/OrderDetails";
import ProductDetail from "./dashboard/ProductDetails";
import CategoriesManager from "./dashboard/CategoriesManager";
import SizesManager from "./dashboard/SizesManager";
import CreateOrder from "./dashboard/CreateOrder";

interface UserInfo {
  email: string;
  fullName: string;
  // Ajoutez d'autres champs si nécessaire
}

const Dashboard: React.FC<DashboardComponentProps> = ({
  darkMode,
  setDarkMode,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      setUserInfo(JSON.parse(storedUserInfo));
    }
  }, []);

  const getComponentName = () => {
    const path = location.pathname.split("/").pop();
    if (path === "dashboard" || path === "") return "Home";
    const safePath = path || "";
    return safePath.charAt(0).toUpperCase() + safePath.slice(1);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('userInfo');
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-screen">
      <div
        className={`flex flex-col md:flex-row h-full ${darkMode ? "dark" : ""}`}
      >
        <Sidebar darkMode={darkMode} setDarkMode={setDarkMode} />
        <div
          className={`flex-1 overflow-y-auto p-4 md:p-8 ${
            darkMode ? "bg-gray-900" : "bg-gray-100"
          } pt-24 sm:pt-24 md:pt-5`}
        >
          <div
            className={`rounded-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            } pt-6 pl-5 pr-5 pb-1 mb-6`}
          >
            <div className="flex justify-between items-center mb-6">
              <h1
                className={`text-2xl md:text-3xl font-bold ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                <span className="relative">
                  Dashboard
                    {location.pathname !== "/dashboard" && (
                    <>
                      <span className="mx-2 text-gray-400">/</span>
                      <span
                      className={`${
                        darkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                      >
                      {location.pathname.match(/\/orders\/[a-f0-9-]+$/i) ? "Commande" : location.pathname.match(/\/clients\/[a-f0-9-]+$/i) ? "Détail Client" : getComponentName()}
                      </span>
                    </>
                    )}
                  <span
                    className={`absolute bottom-0 left-0 h-1 w-12 ${
                      darkMode ? "bg-blue-500" : "bg-blue-600"
                    } rounded-full`}
                  ></span>
                </span>
              </h1>
              <div className="flex items-center space-x-4">
                <button
                  className={`p-2 rounded-lg 
                  hover:bg-blue-500 hover:bg-opacity-10`}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                </button>
                <div className="relative">
                  <button
                    className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-blue-500 hover:bg-opacity-10`}
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <User size={20} />
                    <span className="hidden md:inline">{userInfo?.fullName}</span>
                    <ChevronDown size={16} />
                  </button>
                  {isUserMenuOpen && (
                    <div
                      className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-10 ${
                        darkMode
                          ? "bg-gray-700 text-white"
                          : "bg-white text-gray-800"
                      } ring-1 ring-black ring-opacity-5`}
                    >
                      <div
                        className="py-1"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        <a
                          href="/profile"
                          className={`block px-4 py-2 text-sm ${
                            darkMode ? "hover:bg-gray-600" : "hover:bg-gray-100"
                          }`}
                          role="menuitem"
                        >
                          Your Profile
                        </a>
                        <a
                          href="/settings"
                          className={`block px-4 py-2 text-sm ${
                            darkMode ? "hover:bg-gray-600" : "hover:bg-gray-100"
                          }`}
                          role="menuitem"
                        >
                          Settings
                        </a>
                        <button
                          onClick={handleSignOut}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            darkMode ? "hover:bg-gray-600" : "hover:bg-gray-100"
                          }`}
                          role="menuitem"
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {!isMobile && (
                  <DarkModeToggle
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                  />
                )}
              </div>
            </div>
          </div>

          <Routes>
            <Route index element={<DashboardHome darkMode={darkMode} />} />
            <Route path="clients" element={<Outlet context={{ darkMode, userInfo }} />}>
              <Route index element={<Clients />} />
              <Route path=":id" element={<ClientDetails />} />
            </Route>
            <Route path="orders" element={<Outlet context={{ darkMode, userInfo }} />}>
              <Route index element={<Orders />} />
              <Route path="add" element={<CreateOrder />} />
              <Route path=":id" element={<OrderDetails />} />
            </Route>
            <Route path="products" element={<Outlet context={{ darkMode, userInfo }} />}>
              <Route index element={<Products />} />
              <Route path="categories" element={<CategoriesManager darkMode={darkMode} />} />
              <Route path="sizes" element={<SizesManager darkMode={darkMode}/>} />
              <Route path=":id" element={<ProductDetail />} />
            </Route>
            <Route
              path="accounting"
              element={<Outlet context={{ darkMode, userInfo }} />}
            >
              <Route index element={<Accounting />} />
            </Route>
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;